import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import AnimatedCounter from "./AnimatedCounter";

const StatCard = ({
  title,
  value,
  icon,
  color = "primary",
  trend,
  trendValue,
  trendColor = "success",
  prefix = "",
  suffix = "",
  decimals = 0,
  animated = true,
  onClick,
  compact = false,
}) => {
  const theme = useTheme();
  
  const getColorValue = (colorName) => {
    const colorMap = {
      primary: theme.palette.primary,
      secondary: theme.palette.secondary,
      success: theme.palette.success,
      error: theme.palette.error,
      warning: theme.palette.warning,
      info: theme.palette.info,
    };
    return colorMap[color] || theme.palette.primary;
  };

  const colorPalette = getColorValue(color);

  if (compact) {
    return (
      <Card
        onClick={onClick}
        sx={{
          height: "100%",
          cursor: onClick ? "pointer" : "default",
          bgcolor: alpha(colorPalette.main, 0.04),
          border: `1px solid ${alpha(colorPalette.main, 0.12)}`,
          borderRadius: 2,
          boxShadow: "none",
          transition: "border-color 0.15s ease, background-color 0.15s ease",
          "&:hover": onClick
            ? {
                borderColor: alpha(colorPalette.main, 0.35),
                bgcolor: alpha(colorPalette.main, 0.08),
              }
            : undefined,
        }}
      >
        <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: alpha(colorPalette.main, 0.12),
                color: colorPalette.main,
                width: 32,
                height: 32,
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: 18 } })}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  lineHeight: 1.2,
                  display: "block",
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: colorPalette.main,
                  fontSize: "1.15rem",
                  lineHeight: 1.25,
                }}
              >
                {prefix}
                {typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: decimals }) : value}
                {suffix}
              </Typography>
              {trend && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette[trendColor]?.main || "text.secondary",
                    fontSize: "0.65rem",
                    fontWeight: 500,
                    lineHeight: 1.2,
                    display: "block",
                  }}
                >
                  {trend}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
      whileHover={{
        y: -8,
        scale: 1.03,
        transition: { duration: 0.2 },
      }}
      style={{ height: "100%" }}
    >
      <Card
        onClick={onClick}
        sx={{
          height: "100%",
          position: "relative",
          overflow: "hidden",
          cursor: onClick ? "pointer" : "default",
          background: `linear-gradient(135deg, ${alpha(colorPalette.main, 0.05)} 0%, ${alpha(colorPalette.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(colorPalette.main, 0.1)}`,
          borderRadius: 3,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${colorPalette.main} 0%, ${colorPalette.light} 100%)`,
            opacity: 0,
            transition: "opacity 0.3s ease-in-out",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: -50,
            right: -50,
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(colorPalette.main, 0.1)} 0%, transparent 70%)`,
            transition: "all 0.3s ease-in-out",
          },
          "&:hover": {
            borderColor: alpha(colorPalette.main, 0.3),
            boxShadow: `0 12px 24px -8px ${alpha(colorPalette.main, 0.25)}, 0 4px 8px -4px ${alpha(colorPalette.main, 0.15)}`,
            transform: "translateY(-4px)",
            "&::before": {
              opacity: 1,
            },
            "&::after": {
              top: -30,
              right: -30,
              width: "180px",
              height: "180px",
            },
          },
        }}
      >
        <CardContent sx={{ p: 2, position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 1,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {title}
              </Typography>

              {animated ? (
                <AnimatedCounter
                  value={value}
                  prefix={prefix}
                  suffix={suffix}
                  decimals={decimals}
                  color={colorPalette.main}
                  size="h3"
                />
              ) : (
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: colorPalette.main,
                    fontSize: { xs: "1.75rem", sm: "2rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {prefix}
                  {typeof value === "number" ? value.toLocaleString() : value}
                  {suffix}
                </Typography>
              )}
            </Box>

            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                delay: 0.3,
                duration: 0.5,
                type: "spring",
                stiffness: 200,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(colorPalette.main, 0.1),
                  color: colorPalette.main,
                  width: 48,
                  height: 48,
                  boxShadow: `0 4px 12px ${alpha(colorPalette.main, 0.2)}`,
                  border: `2px solid ${alpha(colorPalette.main, 0.2)}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: alpha(colorPalette.main, 0.15),
                    transform: "scale(1.1) rotate(5deg)",
                  },
                }}
              >
                {React.cloneElement(icon, { fontSize: "small" })}
              </Avatar>
            </motion.div>
          </Box>

          {trend && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <Chip
                label={trend}
                size="small"
                color={trendColor}
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  height: "24px",
                  borderRadius: "12px",
                  bgcolor: alpha(theme.palette[trendColor]?.main || theme.palette.success.main, 0.1),
                  color: theme.palette[trendColor]?.main || theme.palette.success.main,
                  border: `1px solid ${alpha(theme.palette[trendColor]?.main || theme.palette.success.main, 0.2)}`,
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />
            </motion.div>
          )}

          {trendValue && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  mt: 1,
                  display: "block",
                  fontSize: "0.7rem",
                }}
              >
                {trendValue}
              </Typography>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default StatCard;
